import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth, isRefreshFor } from './AuthContext';
import { CloseIcon, MailIcon, PaperclipIcon, SearchIcon, SettingsIcon, CheckCircleIcon } from './Icons';
import EmailThread, { PriorityTag } from './EmailThread';
import EmailMailbox from './EmailMailbox';
import EmailComposer from './EmailComposer';
import EmailCustomerPanel from './EmailCustomerPanel';
import EmailSettings from './EmailSettings';
import ListSkeleton from './LoadingSkeleton';
import { avatarColor, avatarInitials } from './emailAvatar';
import { EMAIL_CATEGORIES, EMAIL_PRIORITIES, EMAIL_STATUS, listTimestamp, startOfTodayIso } from './emailUtils';

// Live updates (SSE) are the main signal; this poll is only a safety net,
// and only while the tab is visible.
const FALLBACK_POLL_MS = 30000;
const PAGE_SIZE = 50;
const STATUS_TABS = [
  { key: 'needs_reply', label: 'Needs reply', count: 'needs_reply' },
  { key: 'waiting_customer', label: 'Waiting', count: 'waiting_customer' },
  { key: 'resolved', label: 'Resolved' },
  { key: 'all', label: 'All', count: 'total' },
];

// Per-browser conveniences only (the chosen tab, the side panel). Storage
// can be unavailable; the page works without it.
const readPref = (key, fallback) => {
  try {
    const value = localStorage.getItem(key);
    return value === null ? fallback : value;
  } catch {
    return fallback;
  }
};
const writePref = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // ignore
  }
};

const useMediaQuery = (query) => {
  const get = () => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(query).matches : false);
  const [matches, setMatches] = useState(get);
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const list = window.matchMedia(query);
    const handle = () => setMatches(list.matches);
    handle();
    list.addEventListener?.('change', handle);
    return () => list.removeEventListener?.('change', handle);
  }, [query]);
  return matches;
};

function ConversationRow({ row, selected, onSelect }) {
  const unread = row.unread_count > 0;
  const statusLabel = EMAIL_STATUS[row.status]?.label || '';
  const display = row.name || row.email || row.number;
  return (
    <button
      type="button"
      role="option"
      aria-selected={selected}
      id={`ew-row-${row.number}`}
      className={`ew-row ew-row-${row.status} ${selected ? 'ew-row-selected' : ''} ${unread ? 'ew-row-unread' : ''}`}
      onClick={() => onSelect(row)}
      aria-label={`${display}. ${statusLabel}. ${unread ? `${row.unread_count} unread. ` : ''}${row.last_subject || ''}`}
    >
      <span className="ew-row-bar" aria-hidden="true" />
      <span className="ew-avatar ew-avatar-sm" style={{ backgroundColor: avatarColor(row.email || row.number) }} aria-hidden="true">
        {avatarInitials(row.name, row.email || row.number)}
      </span>
      <span className="ew-row-main">
        <span className="ew-row-top">
          <span className="ew-row-name">{display}</span>
          <PriorityTag priority={row.priority} />
          <span className="ew-row-time">{listTimestamp(row.last_message_at)}</span>
        </span>
        <span className="ew-row-bottom">
          <span className="ew-row-text">
            {row.last_direction === 'outbound' && <span className="ew-row-you">You: </span>}
            <span className="ew-row-subject">{row.last_subject || '(no subject)'}</span>
            {row.preview && <span className="ew-row-preview"> — {row.preview}</span>}
          </span>
          {row.has_attachments && <PaperclipIcon width={12} height={12} className="ew-row-clip" aria-hidden="true" />}
          {unread && <span className="ew-unread-badge">{row.unread_count}</span>}
        </span>
      </span>
    </button>
  );
}

// Email workspace: conversations (left), the open conversation (middle) and
// the customer (right), plus the live Mailbox view on its own tab. Lists are
// filtered and paged on the server (GET /api/email/conversations?limit=…),
// so the page stays quick however much mail there is.
function EmailOverview({ calls = [], onOpenWhatsapp }) {
  const { authFetch, subscribeRefresh, user } = useAuth();
  // The customer panel sits beside the thread from 1440px; below that it's a
  // drawer that starts closed, so it never covers the email being read.
  const wide = useMediaQuery('(min-width: 1440px)');
  const narrow = useMediaQuery('(max-width: 899px)');

  const [mode, setMode] = useState('conversations');
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState(null);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');

  const [status, setStatus] = useState(() => readPref('email_status_tab', 'needs_reply'));
  const [chips, setChips] = useState({ unread: false, attachments: false, today: false, mine: false });
  const [priority, setPriority] = useState('');
  const [category, setCategory] = useState('');
  const [sort, setSort] = useState('newest');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  const [selectedKey, setSelectedKey] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [panelOpen, setPanelOpen] = useState(() => readPref('email_ctx_panel_open', 'true') !== 'false');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [assignees, setAssignees] = useState([]);
  const [signature, setSignature] = useState('');

  const listRef = useRef(null);

  // Debounced search: the server is asked once typing pauses.
  useEffect(() => {
    const term = searchInput.trim();
    const timer = setTimeout(() => setSearch(term.length >= 2 ? term : ''), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    authFetch('/api/email/assignees')
      .then((response) => (response.ok ? response.json() : []))
      .then((data) => setAssignees(Array.isArray(data) ? data : []))
      .catch(() => {});
    authFetch('/api/email/me/settings')
      .then((response) => (response.ok ? response.json() : {}))
      .then((data) => setSignature(data.signature || ''))
      .catch(() => {});
  }, [authFetch]);

  const query = useMemo(() => {
    const params = new URLSearchParams({ counts: '1' });
    if (status !== 'all') params.set('status', status);
    if (chips.unread) params.set('unread', '1');
    if (chips.attachments) params.set('attachments', '1');
    if (chips.today) params.set('since', startOfTodayIso());
    if (chips.mine) params.set('assignee', 'me');
    if (priority) params.set('priority', priority);
    if (category) params.set('category', category);
    if (sort === 'oldest') params.set('sort', 'oldest');
    if (search) params.set('q', search);
    return params.toString();
  }, [status, chips, priority, category, sort, search]);

  const queryRef = useRef(query);
  queryRef.current = query;
  const loadedRef = useRef(0);
  loadedRef.current = items.length;
  const syncRef = useRef({ inFlight: false, queued: false, debounce: null });

  // Reloads the list (as many rows as are loaded, so paging isn't undone).
  // One request at a time; anything asked for meanwhile becomes one more.
  const fetchList = useCallback(
    async ({ silent = true } = {}) => {
      const sync = syncRef.current;
      if (sync.inFlight) {
        sync.queued = true;
        return;
      }
      sync.inFlight = true;
      const requested = queryRef.current;
      if (!silent) setRefreshing(true);
      try {
        const params = new URLSearchParams(requested);
        params.set('limit', String(Math.min(200, Math.max(PAGE_SIZE, loadedRef.current))));
        const response = await authFetch(`/api/email/conversations?${params}`);
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Failed to load conversations');
        if (requested !== queryRef.current) {
          sync.queued = true;
          return;
        }
        setItems(data.items || []);
        setNextCursor(data.nextCursor || null);
        if (data.counts) setCounts(data.counts);
        setError('');
      } catch (err) {
        setError(err.message || 'Failed to load conversations');
      } finally {
        setLoading(false);
        setRefreshing(false);
        sync.inFlight = false;
        if (sync.queued) {
          sync.queued = false;
          fetchList();
        }
      }
    },
    [authFetch]
  );

  // A filter change starts from the first page again.
  useEffect(() => {
    loadedRef.current = 0;
    fetchList({ silent: false });
  }, [query, fetchList]);

  useEffect(() => {
    const schedule = () => {
      const sync = syncRef.current;
      clearTimeout(sync.debounce);
      sync.debounce = setTimeout(() => fetchList(), 250);
    };
    const unsubscribe = subscribeRefresh((event) => {
      if (isRefreshFor(event, 'email')) schedule();
    });
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') fetchList();
    }, FALLBACK_POLL_MS);
    const handleVisible = () => document.visibilityState === 'visible' && fetchList();
    document.addEventListener('visibilitychange', handleVisible);
    return () => {
      unsubscribe();
      clearInterval(interval);
      clearTimeout(syncRef.current.debounce);
      document.removeEventListener('visibilitychange', handleVisible);
    };
  }, [subscribeRefresh, fetchList]);

  const loadMore = async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const params = new URLSearchParams(queryRef.current);
      params.set('limit', String(PAGE_SIZE));
      params.set('cursor', nextCursor);
      params.delete('counts');
      const response = await authFetch(`/api/email/conversations?${params}`);
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to load more');
      setItems((current) => {
        const seen = new Set(current.map((row) => row.number));
        return [...current, ...(data.items || []).filter((row) => !seen.has(row.number))];
      });
      setNextCursor(data.nextCursor || null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleListScroll = (event) => {
    const el = event.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 200) loadMore();
  };

  const selectedKeyRef = useRef(selectedKey);
  selectedKeyRef.current = selectedKey;
  // A fresher copy of one conversation (after a state change or a thread
  // load). Kept for the open conversation even when it isn't in the list.
  const replaceRow = useCallback((row) => {
    if (!row) return;
    setItems((current) => current.map((item) => (item.number === row.number ? { ...item, ...row } : item)));
    if (row.number === selectedKeyRef.current) setSelectedRow((current) => ({ ...(current || {}), ...row }));
  }, []);

  const handleSelect = (row) => {
    setSelectedKey(row.number);
    setSelectedRow(row);
    setDrawerOpen(false);
  };

  const handleRead = useCallback((key) => {
    setItems((current) => current.map((item) => (item.number === key ? { ...item, unread_count: 0 } : item)));
  }, []);

  const handleMovedTo = useCallback(
    (newKey) => {
      setSelectedKey(newKey);
      setSelectedRow(null);
      fetchList();
    },
    [fetchList]
  );

  const handleMarkedUnread = useCallback(() => {
    setSelectedKey(null);
    setSelectedRow(null);
    fetchList();
  }, [fetchList]);

  const togglePanel = () => {
    if (!wide) {
      setDrawerOpen((open) => !open);
      return;
    }
    setPanelOpen((open) => {
      writePref('email_ctx_panel_open', String(!open));
      return !open;
    });
  };
  const showPanel = wide ? panelOpen : drawerOpen;

  const chooseStatus = (key) => {
    setStatus(key);
    writePref('email_status_tab', key);
  };

  const toggleChip = (key) => setChips((current) => ({ ...current, [key]: !current[key] }));

  const extraFilters = (priority ? 1 : 0) + (category ? 1 : 0) + (sort !== 'newest' ? 1 : 0);

  // Up/Down move through the list.
  const handleListKeyDown = (event) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    event.preventDefault();
    const index = items.findIndex((row) => row.number === selectedKey);
    const next = items[event.key === 'ArrowDown' ? Math.min(items.length - 1, index + 1) : Math.max(0, index - 1)];
    if (next) {
      handleSelect(next);
      document.getElementById(`ew-row-${next.number}`)?.scrollIntoView({ block: 'nearest' });
    }
  };

  const selected = items.find((row) => row.number === selectedKey) || selectedRow || (selectedKey ? { number: selectedKey } : null);
  const toSuggestions = useMemo(
    () => items.filter((row) => row.email).slice(0, 50).map((row) => ({ email: row.email, name: row.name })),
    [items]
  );

  const updateSelectedState = async (changes) => {
    const response = await authFetch(`/api/email/conversations/${encodeURIComponent(selectedKey)}/state`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(changes),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Could not save');
    replaceRow(data);
  };

  const showList = !narrow || !selectedKey;
  const showThread = !narrow || Boolean(selectedKey);
  const panelAsDrawer = !wide;

  const emptyMessage = () => {
    if (search) return `No conversations match "${search}".`;
    if (status === 'needs_reply' && !chips.unread && !chips.attachments && !chips.today && !chips.mine && !extraFilters) {
      return 'Nothing is waiting for a reply.';
    }
    return 'No conversations match these filters.';
  };

  return (
    <div className="ew-page">
      <div className="ew-topbar">
        <div className="ew-mode-tabs" role="tablist" aria-label="Email view">
          <button type="button" role="tab" aria-selected={mode === 'conversations'} className={mode === 'conversations' ? 'ew-mode-active' : ''} onClick={() => setMode('conversations')}>
            Conversations
          </button>
          <button type="button" role="tab" aria-selected={mode === 'mailbox'} className={mode === 'mailbox' ? 'ew-mode-active' : ''} onClick={() => setMode('mailbox')}>
            Mailbox
          </button>
        </div>
        <div className="ew-topbar-actions">
          <button type="button" className="btn btn-primary btn-small" onClick={() => setComposeOpen(true)}>
            <MailIcon width={14} height={14} />
            New email
          </button>
          <button type="button" className="ew-icon-btn" onClick={() => setSettingsOpen(true)} title="Email settings" aria-label="Email settings">
            <SettingsIcon width={15} height={15} />
          </button>
        </div>
      </div>

      {mode === 'mailbox' ? (
        <EmailMailbox signature={signature} />
      ) : (
        <div className={`ew-workspace ${narrow ? 'ew-workspace-narrow' : ''}`}>
          {showList && (
            <div className="ew-list-pane">
              <div className="ew-list-tools">
                <div className="search-input-wrap ew-search">
                  <SearchIcon className="search-input-icon" width={15} height={15} />
                  <input
                    type="search"
                    className="search-input"
                    placeholder="Search name, email, phone, subject…"
                    value={searchInput}
                    onChange={(event) => setSearchInput(event.target.value)}
                    aria-label="Search email conversations"
                  />
                </div>
                <div className="ew-status-tabs" role="tablist" aria-label="Reply status">
                  {STATUS_TABS.map((tab) => {
                    const count = tab.count && counts ? counts[tab.count] : null;
                    return (
                      <button
                        type="button"
                        key={tab.key}
                        role="tab"
                        aria-selected={status === tab.key}
                        className={`ew-status-tab ew-status-tab-${tab.key} ${status === tab.key ? 'ew-status-tab-active' : ''}`}
                        onClick={() => chooseStatus(tab.key)}
                      >
                        {tab.label}
                        {count != null && <span className="ew-status-tab-count">{count}</span>}
                      </button>
                    );
                  })}
                </div>
                <div className="ew-chips" role="group" aria-label="Filters">
                  {[
                    ['unread', `Unread${counts?.unread ? ` ${counts.unread}` : ''}`],
                    ['attachments', 'Attachments'],
                    ['today', 'Today'],
                    ['mine', `Mine${counts?.mine ? ` ${counts.mine}` : ''}`],
                  ].map(([key, label]) => (
                    <button
                      type="button"
                      key={key}
                      className={`ew-chip ${chips[key] ? 'ew-chip-active' : ''}`}
                      aria-pressed={chips[key]}
                      onClick={() => toggleChip(key)}
                    >
                      {label}
                    </button>
                  ))}
                  <span className="ew-filters-anchor">
                    <button
                      type="button"
                      className={`ew-chip ${extraFilters ? 'ew-chip-active' : ''}`}
                      onClick={() => setFiltersOpen((open) => !open)}
                      aria-expanded={filtersOpen}
                    >
                      More{extraFilters ? ` (${extraFilters})` : ''}
                    </button>
                    {filtersOpen && (
                      <div className="ew-filters-pop">
                        <label>
                          <span>Priority</span>
                          <select value={priority} onChange={(event) => setPriority(event.target.value)}>
                            <option value="">Any</option>
                            {EMAIL_PRIORITIES.map((item) => (
                              <option key={item.value} value={item.value}>
                                {item.label}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label>
                          <span>Category</span>
                          <select value={category} onChange={(event) => setCategory(event.target.value)}>
                            <option value="">Any</option>
                            {EMAIL_CATEGORIES.map((item) => (
                              <option key={item.value} value={item.value}>
                                {item.label}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label>
                          <span>Order</span>
                          <select value={sort} onChange={(event) => setSort(event.target.value)}>
                            <option value="newest">Newest activity first</option>
                            <option value="oldest">Oldest activity first</option>
                          </select>
                        </label>
                        <div className="ew-filters-actions">
                          <button
                            type="button"
                            className="link-btn"
                            onClick={() => {
                              setPriority('');
                              setCategory('');
                              setSort('newest');
                            }}
                          >
                            Clear
                          </button>
                          <button type="button" className="btn btn-secondary btn-small" onClick={() => setFiltersOpen(false)}>
                            Done
                          </button>
                        </div>
                      </div>
                    )}
                  </span>
                </div>
              </div>
              {error && (
                <div className="ew-banner ew-banner-error">
                  {error}
                  <button type="button" className="link-btn" onClick={() => fetchList({ silent: false })}>
                    Retry
                  </button>
                </div>
              )}
              <div
                className={`ew-list ${refreshing ? 'ew-list-refreshing' : ''}`}
                ref={listRef}
                role="listbox"
                aria-label="Email conversations"
                tabIndex={0}
                onKeyDown={handleListKeyDown}
                onScroll={handleListScroll}
              >
                {loading ? (
                  <ListSkeleton />
                ) : items.length === 0 ? (
                  <div className="ew-list-empty">
                    {status === 'needs_reply' && !search && <CheckCircleIcon width={28} height={28} />}
                    <p>{emptyMessage()}</p>
                    {status !== 'all' && (
                      <button type="button" className="link-btn" onClick={() => chooseStatus('all')}>
                        Show all conversations
                      </button>
                    )}
                  </div>
                ) : (
                  <>
                    {items.map((row) => (
                      <ConversationRow key={row.number} row={row} selected={row.number === selectedKey} onSelect={handleSelect} />
                    ))}
                    {nextCursor && (
                      <button type="button" className="ew-load-more" onClick={loadMore} disabled={loadingMore}>
                        {loadingMore ? 'Loading…' : 'Load more'}
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {showThread && (
            <div className="ew-thread-pane">
              {selected ? (
                <EmailThread
                  key={selected.number}
                  conversationKey={selected.number}
                  summary={selected}
                  signature={signature}
                  onConversationUpdate={replaceRow}
                  onMovedTo={handleMovedTo}
                  onRead={handleRead}
                  onMarkedUnread={handleMarkedUnread}
                  onBack={narrow ? () => setSelectedKey(null) : null}
                  panelOpen={showPanel}
                  onTogglePanel={togglePanel}
                />
              ) : (
                <div className="ew-placeholder">
                  <MailIcon width={40} height={40} />
                  <p>Select a conversation to read and reply.</p>
                  <p className="ew-muted">↑ ↓ move through the list · Ctrl+Enter sends a reply</p>
                </div>
              )}
            </div>
          )}

          {selected && showPanel && showThread && (
            <>
              {panelAsDrawer && <div className="ew-drawer-backdrop" onClick={togglePanel} aria-hidden="true" />}
              <div className={`ew-panel-pane ${panelAsDrawer ? 'ew-panel-drawer' : ''}`}>
                <EmailCustomerPanel
                  key={selected.number}
                  conversationKey={selected.number}
                  conversation={items.find((row) => row.number === selected.number) || selectedRow}
                  calls={calls}
                  assignees={assignees}
                  onUpdateState={updateSelectedState}
                  onOpenWhatsapp={onOpenWhatsapp}
                  onLinked={handleMovedTo}
                  onClose={panelAsDrawer ? togglePanel : null}
                />
              </div>
            </>
          )}
        </div>
      )}

      {composeOpen && (
        <div className="modal-overlay modal-overlay-center" onClick={() => setComposeOpen(false)}>
          <div className="modal-panel ew-compose-modal" onClick={(event) => event.stopPropagation()} role="dialog" aria-label="New email">
            <div className="modal-header">
              <h2>New email</h2>
              <button className="modal-close" onClick={() => setComposeOpen(false)} aria-label="Close">
                <CloseIcon width={18} height={18} />
              </button>
            </div>
            <EmailComposer
              mode="new"
              toSuggestions={toSuggestions}
              signature={signature}
              templateValues={{ agent_name: user?.agentName || user?.username || '' }}
              draftKey="new-email"
              onCancel={() => setComposeOpen(false)}
              onSent={(result) => {
                setComposeOpen(false);
                setMode('conversations');
                setSelectedKey(result.conversationKey);
                setSelectedRow(null);
                fetchList();
              }}
            />
          </div>
        </div>
      )}

      {settingsOpen && (
        <EmailSettings onClose={() => setSettingsOpen(false)} signature={signature} onSignatureSaved={setSignature} />
      )}
    </div>
  );
}

export default EmailOverview;
